<template>
  <div class="date-pick__date-panel">
    <!-- date header-->
    <div class="date-pick__header">
      <a v-if="hasPrev" :title="getJumpTitle('prev')" class="date-pick__cmd date-pick__cmd--prev" @click.stop="goPrevMonth">
        <i class="aid aid-chevron-left"></i>
      </a>
      <span>
        <a class="date-picker__header-label mr-2"
           @click.stop="showMonthPicker">{{ getMonthStr(drawMonth) }}</a>
        <a class="date-picker__header-label"
           @click.stop="showYearPicker">{{ drawYear }}</a>
      </span>
      <a v-if="hasNext" :title="getJumpTitle('next')" class="date-pick__cmd date-pick__cmd--next" @click.stop="goNextMonth"> <i class="aid aid-chevron-right"></i></a>
    </div>
    <!-- date body-->
    <div class="date-pick__body">
      <table @click.stop="handleClick">
        <thead>
          <tr>
            <th>
              <span>{{ t('t.datepicker.weeks.sun') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.mon') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.tue') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.wed') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.thu') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.fri') }}</span>
            </th>
            <th>
              <span>{{ t('t.datepicker.weeks.sat') }}</span>
            </th>
          </tr>
        </thead>
        <tbody @mousemove="handleMouseMove">
          <tr v-for="(week, weekIndex) in 6" :key="`week${weekIndex}`">
            <td v-for="(day, dayIndex) in 7" :key="`day${dayIndex}`" :class="getCellClasses(cells[(week-1)*7 + (day-1)])">
              <a :index="(week-1)*7 + (day-1)" href="javascript:void(0)">
                {{ cells[(week-1)*7 + (day-1)].text }}</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
/**
 * 所有日期都用moment的格式
 */
<script>
import moment from 'moment'
import { clearHours, hasClass } from '../util.js'
import locale from '../../../mixins/locale'
import {
  PREFIX_DATE_PICK_CLASS,
  CLASS_SELECTED,
  CLASS_DISABLED,
  CLASS_TODAY
} from '../constants'

const CLASS_DATE = `${PREFIX_DATE_PICK_CLASS}__date`
const CLASS_IN_SELECTED = `${PREFIX_DATE_PICK_CLASS}--in-selected`
const CLASS_OTHER_MONTH = `${PREFIX_DATE_PICK_CLASS}__other-month`
const CLASS_PREV_MONTH = `${PREFIX_DATE_PICK_CLASS}__prev-month`
const CLASS_NEXT_MONTH = `${PREFIX_DATE_PICK_CLASS}__next-month`
const CLASS_IS_MIN = `${PREFIX_DATE_PICK_CLASS}--selected-min`
const CLASS_IS_MAX = `${PREFIX_DATE_PICK_CLASS}--selected-max`
const CLASS_NOT_IN_RANGE = `${PREFIX_DATE_PICK_CLASS}__not-in-range` // 只有这个样式是灰色字体

export default {
  name: 'DateTable',
  mixins: [locale],
  props: {
    defaultDrawDate: {
      type: Object
    },
    minSelected: {
      type: Object
    },
    maxSelected: {
      type: Object
    },
    minSelectRange: {
      type: Object
    },
    maxSelectRange: {
      type: Object
    },
    isRange: {
      type: Boolean,
      default: false
    },
    rangeState: {
      type: Object,
      default() {
        return {
          endDate: null,
          selecting: false
        }
      }
    },
    hasPrev: {
      type: Boolean,
      default: true
    },
    hasNext: {
      type: Boolean,
      default: true
    },
    confirm: Boolean,
    unselectableDaysOfWeek: {
      type: Array,
      default: () => []
    },
    unselectableDays: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      cells: [],
      selectedDates: [],
      drawDate: this.defaultDrawDate,
      drawMonth: this.defaultDrawDate.month() + 1,
      drawYear: this.defaultDrawDate.year()
    }
  },
  computed: {
    weeks() {
      return moment.localeData().weekdaysShort()
    }
  },
  watch: {
    unselectableDaysOfWeek() {
      this.generateDates()
    },
    unselectableDays() {
      this.generateDates()
    },
    defaultDrawDate(newVal) {
      this.updateCalendar(newVal)
    },
    'rangeState.endDate'(newVal) {
      let newTime = clearHours(newVal)
      let minTime = clearHours(this.minSelected)
      if (newTime > minTime) {
        this.markRange(newVal)
      }
    },
    minSelected(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.rangeState.selecting = true
        this.markRange(newVal)
      } else if (!newVal) {
        this.rangeState.selecting = false
        this.markRange(newVal)
      } else {
        this.markRange(newVal)
      }
    },
    maxSelected(newVal, oldVal) {
      if (newVal) {
        this.rangeState.selecting = false
        this.markRange(newVal)
      }
    },
    minSelectRange() {
      this.init()
    },
    maxSelectRange() {
      this.init()
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.generateDates()
    },
    /**
     * 日期是否不可选
     * @private
     * @param  {Number}  day   一个0--6的数字，表示当前星期数(从0开始，0表示周日，1表示周一，依此类推)
     * @param  {Number}  date  需要验证的日期，格式为YYYY-MM-DD
     * @return {Boolean} 日期不可选返回true，否则返回false
     */
    unselectable(day, date) {
      let dateStr = moment(date).format('YYYY-MM-DD')
      if (this.unselectableDaysOfWeek.length > 0) { // unselectableDaysOfWeek 优先级更高
        return this.unselectableDaysOfWeek.includes(day)
      } else if (this.unselectableDays.length > 0) {
        return this.unselectableDays.includes(dateStr)
      }
      return false
    },
    getCellClasses(cell) {
      let classes = []
      classes.push(CLASS_DATE)
      if (cell.type === 'today' && !cell.disabled) {
        classes.push(CLASS_TODAY)
      }
      if (cell.otherMonth && !cell.disabled) {
        classes.push(CLASS_OTHER_MONTH)
        if (cell.type === 'prev-month') {
          classes.push(CLASS_PREV_MONTH)
        }
        if (cell.type === 'next-month') {
          classes.push(CLASS_NEXT_MONTH)
        }
      }
      if (cell.selected === true) {
        if (this.isRange === true) {
          if (!cell.otherMonth) {
            classes.push(CLASS_SELECTED)
          }
        } else {
          classes.push(CLASS_SELECTED)
        }
      }
      if (cell.inSelected === true && !cell.otherMonth) {
        classes.push(CLASS_IN_SELECTED)
      }
      if (cell.notInRange) {
        classes.push(CLASS_NOT_IN_RANGE)
      }
      if (cell.disabled) {
        classes.push(CLASS_DISABLED)
      }
      if (this.isRange === true) {
        if (cell.isMin && !cell.otherMonth) {
          classes.push(CLASS_IS_MIN)
        }
        if (cell.isMax && !cell.otherMonth) {
          classes.push(CLASS_IS_MAX)
        }
      }
      return classes.join(' ')
    },
    updateCalendar(drawDate) {
      this.drawDate = drawDate
      if (drawDate && drawDate.isValid()) {
        this.drawMonth = drawDate.month() + 1
        this.drawYear = drawDate.year()
        this.generateDates()
      }
    },
    generateDates() {
      this.cells = []
      let now = moment({
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        date: new Date().getDate()
      })
      let firstDay = 0
      // let date = moment({year: this.drawDate.year(), month: this.drawDate.month(), date: 1})
      let date = moment(
        `${this.drawDate.year()}-${this.drawDate.month() + 1}-1`,
        'YYYY-MM-DD'
      )
      let leadDays = (date.day() - firstDay + 7) % 7
      if (leadDays) {
        let _periods = leadDays - (date.day() === firstDay ? 7 : 0)
        date.subtract(_periods, 'd')
      }
      let row = null
      let isToday = false
      for (let week = 0; week < 6; week++) {
        for (let day = 0; day < 7; day++) {
          row = {
            date: moment(
              `${date.year()}-${date.month() + 1}-${date.date()}`,
              'YYYY-MM-DD'
            ),
            text: date.date(),
            otherMonth: date.month() !== this.drawDate.month(),
            disabled: false,
            selected: false,
            type: 'normal',
            notInRange: false
          }
          isToday = now.toDate().getTime() === date.toDate().getTime()
          if (isToday) {
            row.type = 'today'
          }
          if (row.otherMonth) {
            if (row.date.isBefore(this.drawDate, 'month')) {
              row.type = 'prev-month'
            } else {
              row.type = 'next-month'
            }
          }
          if (this.minSelectRange && date.isBefore(this.minSelectRange, 'date')) {
            row.disabled = true
            row.notInRange = true
          }
          if (this.maxSelectRange && date.isAfter(this.maxSelectRange, 'date')) {
            row.disabled = true
            row.notInRange = true
          }
          if (this.unselectable(day, date)) {
            row.disabled = true
            row.notInRange = true
          }
          this.cells.push(row)
          date.add(1, 'd')
        }
      }
      this.markRange()
    },
    markRange(maxDate) {
      let minDate = this.minSelected
      maxDate = maxDate || this.maxSelected
      let minDay = clearHours(minDate)
      let maxDay = clearHours(maxDate)
      this.cells.forEach(cell => {
        if (cell.type === 'today' || cell.type === 'normal') {
          let time = clearHours(cell.date)
          cell.inSelected = !!(time > minDay && time < maxDay)
          cell.isMin = !!(minDate && time === minDay)
          cell.isMax = !!(maxDate && time === maxDay)
          cell.selected = !!(time === minDay || time === maxDay)
        }
      })
    },
    handleMouseMove(event) {
      if (!this.isRange) {
        return
      }
      if (!this.rangeState.selecting) return
      if (event.target.tagName !== 'A') return
      if (hasClass(event.target.parentElement, CLASS_DISABLED)) return
      // 范围选择的时候显示背景
      if (this.isRange === true) {
        let index = event.target.getAttribute('index')
        this.rangeState.endDate = this.cells[index].date
        this.$emit('changerange', {
          minDate: this.minSelected,
          maxDate: this.maxSelected,
          rangeState: this.rangeState
        })
      }
    },
    handleClick(event) {
      let target = event.target
      if (target.tagName !== 'A') return
      let index = parseInt(target.getAttribute('index'))
      if (hasClass(target.parentElement, CLASS_DISABLED)) return
      let newDate = this.cells[index]
      if (this.isRange === true) {
        // range date
        newDate.selected = true
        this.rangeState.selecting = true
        let newDateTime = clearHours(newDate.date)
        if (this.minSelected && this.maxSelected) {
          let maxTime = clearHours(this.maxSelected)
          if (newDateTime === maxTime) {
            this.rangeState.selecting = false
            this.selectedDates = [this.minSelected, newDate.date]
          } else {
            let minDate = newDate.date
            let maxDate = null
            this.selectedDates = [minDate, maxDate]
          }
        } else if (this.minSelected && !this.maxSelected) {
          let minTime = clearHours(this.minSelected)
          if (newDateTime >= minTime) {
            this.rangeState.selecting = false
            this.selectedDates = [this.minSelected, newDate.date]
          } else {
            this.selectedDates = [newDate.date, this.maxSelected]
          }
        } else if (!this.minSelected) {
          let minDate = newDate.date
          this.selectedDates = [minDate, this.maxSelected]
        }
      } else {
        // single date
        newDate.selected = true
        this.selectedDates = [newDate.date, null]
        if (newDate.otherMonth === true) {
          // 重新渲染日历,选中
          if (newDate.type === 'prev-month') {
            this.goPrevMonth()
          } else if (newDate.type === 'next-month') {
            this.goNextMonth()
          }
        }
      }
      this.$emit('pick', {
        minDate: this.selectedDates[0],
        maxDate: this.selectedDates[1],
        rangeState: this.rangeState
      })
    },
    getMonthStr(month) {
      if (!month) {
        month = this.drawDate.month()
      }
      return this.t(`t.datepicker.month${month}`)
    },
    // 跳转到上个月
    goPrevMonth() {
      this.drawDate.subtract(1, 'M')
      if (this.isRange === true) {
        this.$emit('changeDrawDate', this.drawDate)
      } else {
        this.updateCalendar(this.drawDate)
      }
    },
    // 跳转到下个月
    goNextMonth() {
      this.drawDate.add(1, 'M')
      if (this.isRange === true) {
        this.$emit('changeDrawDate', this.drawDate)
      } else {
        this.updateCalendar(this.drawDate)
      }
    },
    // 显示年面板
    showYearPicker() {
      this.$emit('changePicker', 'year')
    },
    // 显示月面板
    showMonthPicker() {
      this.$emit('changePicker', 'month')
    },
    getJumpTitle(type) {
      return this.t(`t.datepicker.${type}MonthBtn`)
    }
  }
}
</script>



// WEBPACK FOOTER //
// date-table.vue?a2614600